import { InfoCard, Progress } from '@backstage/core-components'
import { Box, Button, IconButton, makeStyles, Theme, Tooltip, Typography } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import GitHubIcon from '@material-ui/icons/GitHub'
import RefreshIcon from '@material-ui/icons/Refresh'

import { PipelineRowData } from '../../../../state'
import { PIPELINE_COLUMNS, PIPELINE_GRID_COLUMNS } from '../layout'
import { PipelineRow } from '../PipelineRow'

const useStyles = makeStyles<Theme>(theme => ({
  headerRow: {
    display: 'grid',
    gridTemplateColumns: PIPELINE_GRID_COLUMNS,
    columnGap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  },
  empty: {
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actionsHeader: {
    textAlign: 'right',
  },
}))

type Props = {
  loading: boolean
  rows: PipelineRowData[]
  projectName: string
  retry: () => void
  page: number
  error?: Error
  hasNextPage: boolean
  hasPreviousPage: boolean
  goToNextPage: () => void
  goToPreviousPage: () => void
}

export const PipelineTable = ({
  loading,
  rows,
  projectName,
  retry,
  page,
  error,
  hasNextPage,
  hasPreviousPage,
  goToNextPage,
  goToPreviousPage,
}: Props) => {
  const classes = useStyles()

  return (
    <InfoCard
      title={
        <Box className={classes.title}>
          <GitHubIcon />
          <Typography variant="h6">{projectName}</Typography>
        </Box>
      }
      action={
        <Tooltip title="Refresh">
          <IconButton onClick={retry} aria-label="refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
      noPadding
    >
      <div className={classes.headerRow}>
        {PIPELINE_COLUMNS.map(col => (
          <span key={col.label} className={col.align === 'right' ? classes.actionsHeader : undefined}>
            {col.label}
          </span>
        ))}
      </div>

      {error && rows.length === 0 ? (
        <div className={classes.empty}>Failed to load pipelines: {error.message}</div>
      ) : loading && rows.length === 0 ? (
        <Box p={3}>
          <Progress />
        </Box>
      ) : rows.length === 0 ? (
        <div className={classes.empty}>No pipelines found for this project yet.</div>
      ) : (
        rows.map(row => <PipelineRow key={row.rowId} pipeline={row.pipeline} workflow={row.workflow} />)
      )}

      <div className={classes.footer}>
        <Typography variant="caption" color="textSecondary">
          Page {page + 1}
        </Typography>
        <Box display="flex" gridGap={8}>
          <Button
            size="small"
            startIcon={<ChevronLeftIcon />}
            disabled={!hasPreviousPage || loading}
            onClick={goToPreviousPage}
          >
            Previous
          </Button>
          <Button size="small" endIcon={<ChevronRightIcon />} disabled={!hasNextPage || loading} onClick={goToNextPage}>
            Next
          </Button>
        </Box>
      </div>
    </InfoCard>
  )
}
